import type {
  FormAnswerRecord,
  FormAnswerValue,
  FormCompletionRecord,
  FormConditionGroup,
  FormConditionRule,
  FormCreateInput,
  FormFieldDefinition,
  FormId,
  FormRecord,
  FormSectionDefinition,
  FormSubmissionInput,
  FormVersionRecord,
} from './form';

export interface FormEngineIssue {
  code:
    | 'invalid_section'
    | 'invalid_field'
    | 'invalid_condition'
    | 'duplicate_field_key'
    | 'missing_required_answer'
    | 'invalid_answer_type'
    | 'invalid_answer_value'
    | 'hidden_field_answer'
    | 'unknown_field'
    | 'invalid_version';
  message: string;
  fieldId?: string;
  sectionId?: string;
}

export interface RenderableFormField {
  field: FormFieldDefinition;
  visible: boolean;
  required: boolean;
  value: FormAnswerValue;
}

export interface RenderableFormSection {
  section: FormSectionDefinition;
  visible: boolean;
  fields: RenderableFormField[];
}

export interface RenderableFormDefinition {
  version: FormVersionRecord;
  sections: RenderableFormSection[];
  visibleFieldIds: string[];
  hiddenFieldIds: string[];
  completion: FormCompletionRecord;
}

function cloneConditionGroup(
  condition: FormConditionGroup | null | undefined,
): FormConditionGroup | null {
  if (!condition) {
    return null;
  }

  return {
    mode: condition.mode,
    rules: condition.rules.map((rule) => ({ ...rule })),
  };
}

function cloneField(field: FormFieldDefinition): FormFieldDefinition {
  return {
    ...field,
    options: field.options.map((option) => ({ ...option })),
    validation: { ...field.validation },
    defaultValue: Array.isArray(field.defaultValue)
      ? [...field.defaultValue]
      : field.defaultValue,
    visibilityCondition: cloneConditionGroup(field.visibilityCondition),
    requiredCondition: cloneConditionGroup(field.requiredCondition),
  };
}

function cloneSection(section: FormSectionDefinition): FormSectionDefinition {
  return {
    ...section,
    visibilityCondition: cloneConditionGroup(section.visibilityCondition),
  };
}

function createSchemaFingerprint(input: {
  sections: FormSectionDefinition[];
  fields: FormFieldDefinition[];
}) {
  return JSON.stringify({
    sections: input.sections,
    fields: input.fields,
  });
}

export function createDefaultFormSection(
  formId: FormId,
): FormSectionDefinition {
  return {
    id: `${formId}:section:default`,
    formId,
    sectionKey: 'default',
    title: 'Main section',
    description: null,
    sortOrder: 100,
    visibilityCondition: null,
  };
}

export function ensureFormVersion(form: FormRecord): FormVersionRecord {
  if (form.currentVersion) {
    return {
      ...form.currentVersion,
      sections: form.currentVersion.sections.map(cloneSection),
      fields: form.currentVersion.fields.map(cloneField),
    };
  }

  const sections =
    form.sections && form.sections.length > 0
      ? form.sections.map(cloneSection)
      : [createDefaultFormSection(form.id)];
  const normalizedFields = form.fields.map((field) =>
    cloneField({
      ...field,
      sectionId: field.sectionId ?? sections[0]?.id ?? null,
      visibilityCondition: field.visibilityCondition ?? null,
      requiredCondition: field.requiredCondition ?? null,
      defaultValue: field.defaultValue ?? null,
      content: field.content ?? null,
      localizationKey: field.localizationKey ?? null,
    }),
  );

  return {
    id: form.currentVersionId ?? `${form.id}:version:1`,
    formId: form.id,
    versionNumber: form.currentVersionNumber ?? 1,
    status: form.status === 'published' ? 'published' : form.status,
    schemaFingerprint: createSchemaFingerprint({
      sections,
      fields: normalizedFields,
    }),
    sections,
    fields: normalizedFields,
    createdAt: form.updatedAt ?? form.createdAt,
    publishedAt: form.status === 'published' ? form.updatedAt : null,
    basedOnVersionId: null,
  };
}

export function ensureFormEngineState(form: FormRecord): FormRecord {
  const currentVersion = ensureFormVersion(form);
  const versions =
    form.versions && form.versions.length > 0
      ? form.versions.map((version) => ({
          ...version,
          sections: version.sections.map(cloneSection),
          fields: version.fields.map(cloneField),
        }))
      : [currentVersion];

  return {
    ...form,
    sections: currentVersion.sections.map(cloneSection),
    fields: currentVersion.fields.map(cloneField),
    currentVersionId: currentVersion.id,
    currentVersionNumber: currentVersion.versionNumber,
    currentVersion,
    versions,
  };
}

export function createFormVersionSnapshot(input: {
  form: FormRecord;
  nextVersionNumber: number;
  sections: FormSectionDefinition[];
  fields: FormFieldDefinition[];
  createdAt: string;
  basedOnVersionId: string | null;
}): FormVersionRecord {
  return {
    id: `${input.form.id}:version:${input.nextVersionNumber}`,
    formId: input.form.id,
    versionNumber: input.nextVersionNumber,
    status:
      input.form.status === 'published'
        ? 'published'
        : input.form.status === 'archived'
          ? 'archived'
          : 'draft',
    schemaFingerprint: createSchemaFingerprint({
      sections: input.sections,
      fields: input.fields,
    }),
    sections: input.sections.map(cloneSection),
    fields: input.fields.map(cloneField),
    createdAt: input.createdAt,
    publishedAt: input.form.status === 'published' ? input.createdAt : null,
    basedOnVersionId: input.basedOnVersionId,
  };
}

function isEmptyAnswer(value: FormAnswerValue) {
  return (
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function compareConditionRule(
  rule: FormConditionRule,
  answersByFieldId: Map<string, FormAnswerValue>,
) {
  const value = answersByFieldId.get(rule.sourceFieldId) ?? null;

  switch (rule.operator) {
    case 'equals':
      return JSON.stringify(value) === JSON.stringify(rule.value ?? null);
    case 'not_equals':
      return JSON.stringify(value) !== JSON.stringify(rule.value ?? null);
    case 'includes':
      return Array.isArray(value)
        ? value.includes(String(rule.value))
        : value === rule.value;
    case 'not_includes':
      return Array.isArray(value)
        ? !value.includes(String(rule.value))
        : value !== rule.value;
    case 'is_truthy':
      return Boolean(value);
    case 'is_falsy':
      return !value;
    default:
      return false;
  }
}

export function evaluateFormConditionGroup(
  condition: FormConditionGroup | null | undefined,
  answersByFieldId: Map<string, FormAnswerValue>,
) {
  if (!condition || condition.rules.length === 0) {
    return true;
  }

  return condition.mode === 'all'
    ? condition.rules.every((rule) =>
        compareConditionRule(rule, answersByFieldId),
      )
    : condition.rules.some((rule) =>
        compareConditionRule(rule, answersByFieldId),
      );
}

export function validateFormDefinitionSchema(
  form: FormRecord | FormCreateInput,
) {
  const issues: FormEngineIssue[] = [];
  const formId = 'id' in form ? form.id : 'draft_form';
  const sections =
    'sections' in form && form.sections && form.sections.length > 0
      ? form.sections.map((section, index) => ({
          id: section.id ?? `${formId}:section:${index + 1}`,
          formId,
          sectionKey: section.sectionKey ?? `section_${index + 1}`,
          title: section.title,
          description: section.description,
          sortOrder: section.sortOrder ?? (index + 1) * 100,
          visibilityCondition: section.visibilityCondition ?? null,
        }))
      : [createDefaultFormSection(formId)];
  const sectionIds = new Set(sections.map((section) => section.id));
  const fields = form.fields.map((field, index) => {
    const fallbackId = `${formId}:field:${index + 1}`;

    return {
      ...field,
      id: field.id ?? fallbackId,
      fieldKey: field.fieldKey ?? field.id ?? `field_${index + 1}`,
      sectionId: field.sectionId ?? sections[0]?.id ?? null,
    };
  });
  const fieldKeys = new Set<string>();
  const fieldIds = new Set(fields.map((field) => field.id));

  for (const field of fields) {
    const fieldKey = field.fieldKey ?? field.id;

    if (fieldKeys.has(fieldKey)) {
      issues.push({
        code: 'duplicate_field_key',
        message: `Field key "${fieldKey}" must be unique within a form version.`,
        fieldId: field.id,
      });
    }

    fieldKeys.add(fieldKey);

    if (field.sectionId && !sectionIds.has(field.sectionId)) {
      issues.push({
        code: 'invalid_section',
        message: 'Field references an unknown section.',
        fieldId: field.id,
        sectionId: field.sectionId,
      });
    }

    if (field.fieldType === 'info' && field.required) {
      issues.push({
        code: 'invalid_field',
        message: 'Informational blocks cannot be required.',
        fieldId: field.id,
      });
    }
  }

  for (const section of sections) {
    for (const rule of section.visibilityCondition?.rules ?? []) {
      if (!fieldIds.has(rule.sourceFieldId)) {
        issues.push({
          code: 'invalid_condition',
          message: 'Section visibility condition references an unknown field.',
          sectionId: section.id,
        });
      }
    }
  }

  for (const field of fields) {
    for (const rule of field.visibilityCondition?.rules ?? []) {
      if (!fieldIds.has(rule.sourceFieldId)) {
        issues.push({
          code: 'invalid_condition',
          message: 'Field visibility condition references an unknown field.',
          fieldId: field.id,
        });
      }
    }

    for (const rule of field.requiredCondition?.rules ?? []) {
      if (!fieldIds.has(rule.sourceFieldId)) {
        issues.push({
          code: 'invalid_condition',
          message: 'Field required condition references an unknown field.',
          fieldId: field.id,
        });
      }
    }
  }

  return {
    valid: issues.length === 0,
    issues,
    normalizedSections: sections,
  };
}

function validateAnswerType(
  field: FormFieldDefinition,
  value: FormAnswerValue,
) {
  if (isEmptyAnswer(value)) {
    return true;
  }

  if (
    ['short_text', 'long_text', 'date', 'datetime', 'email', 'phone'].includes(
      field.fieldType,
    )
  ) {
    return typeof value === 'string';
  }

  if (field.fieldType === 'number') {
    return typeof value === 'number';
  }

  if (field.fieldType === 'boolean') {
    return typeof value === 'boolean';
  }

  if (field.fieldType === 'single_select') {
    return typeof value === 'string';
  }

  if (field.fieldType === 'multi_select') {
    return Array.isArray(value);
  }

  return value === null;
}

function validateFieldValue(
  field: FormFieldDefinition,
  value: FormAnswerValue,
) {
  const issues: FormEngineIssue[] = [];

  if (!validateAnswerType(field, value)) {
    issues.push({
      code: 'invalid_answer_type',
      message: `Field "${field.fieldKey}" received an invalid answer type.`,
      fieldId: field.id,
    });
    return issues;
  }

  if (isEmptyAnswer(value)) {
    return issues;
  }

  if (typeof value === 'string') {
    if (
      field.validation.minLength !== null &&
      field.validation.minLength !== undefined &&
      value.length < field.validation.minLength
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" is shorter than allowed.`,
        fieldId: field.id,
      });
    }

    if (
      field.validation.maxLength !== null &&
      field.validation.maxLength !== undefined &&
      value.length > field.validation.maxLength
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" is longer than allowed.`,
        fieldId: field.id,
      });
    }

    if (field.validation.pattern) {
      const pattern = new RegExp(field.validation.pattern);

      if (!pattern.test(value)) {
        issues.push({
          code: 'invalid_answer_value',
          message: `Field "${field.fieldKey}" does not match the required format.`,
          fieldId: field.id,
        });
      }
    }

    if (
      field.fieldType === 'email' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value)
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" requires a valid email address.`,
        fieldId: field.id,
      });
    }

    if (field.fieldType === 'phone' && !/^[0-9+()\-\s]{7,}$/u.test(value)) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" requires a valid phone number.`,
        fieldId: field.id,
      });
    }
  }

  if (typeof value === 'number') {
    if (
      field.validation.minValue !== null &&
      field.validation.minValue !== undefined &&
      value < field.validation.minValue
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" is below the minimum value.`,
        fieldId: field.id,
      });
    }

    if (
      field.validation.maxValue !== null &&
      field.validation.maxValue !== undefined &&
      value > field.validation.maxValue
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" is above the maximum value.`,
        fieldId: field.id,
      });
    }
  }

  if (
    (field.fieldType === 'single_select' ||
      field.fieldType === 'multi_select') &&
    field.options.length > 0
  ) {
    const allowed = new Set(field.options.map((option) => option.value));
    const selected = Array.isArray(value) ? value : [value];

    if (selected.some((entry) => !allowed.has(String(entry)))) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" contains an unknown option.`,
        fieldId: field.id,
      });
    }

    if (
      Array.isArray(value) &&
      field.validation.minSelections !== null &&
      field.validation.minSelections !== undefined &&
      value.length < field.validation.minSelections
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" has fewer selections than allowed.`,
        fieldId: field.id,
      });
    }

    if (
      Array.isArray(value) &&
      field.validation.maxSelections !== null &&
      field.validation.maxSelections !== undefined &&
      value.length > field.validation.maxSelections
    ) {
      issues.push({
        code: 'invalid_answer_value',
        message: `Field "${field.fieldKey}" has more selections than allowed.`,
        fieldId: field.id,
      });
    }
  }

  return issues;
}

export function resolveRenderableForm(
  form: FormRecord,
  answers: FormAnswerRecord[] = [],
): RenderableFormDefinition {
  const engineForm = ensureFormEngineState(form);
  const version = engineForm.currentVersion!;
  const answersByFieldId = new Map(
    answers.map((answer) => [answer.fieldId, answer.value]),
  );
  const sections = version.sections
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((section) => {
      const sectionVisible = evaluateFormConditionGroup(
        section.visibilityCondition,
        answersByFieldId,
      );
      const fields = version.fields
        .filter((field) => (field.sectionId ?? section.id) === section.id)
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((field) => {
          const visible =
            sectionVisible &&
            evaluateFormConditionGroup(
              field.visibilityCondition,
              answersByFieldId,
            );
          const required =
            visible &&
            (field.required ||
              (field.requiredCondition
                ? evaluateFormConditionGroup(
                    field.requiredCondition,
                    answersByFieldId,
                  )
                : false));

          return {
            field,
            visible,
            required,
            value: answersByFieldId.get(field.id) ?? field.defaultValue ?? null,
          };
        });

      return {
        section,
        visible: sectionVisible && fields.some((field) => field.visible),
        fields,
      };
    });

  const visibleFields = sections.flatMap((section) =>
    section.fields.filter((field) => field.visible),
  );
  const requiredVisibleFields = visibleFields.filter((field) => field.required);
  const answeredRequiredFields = requiredVisibleFields.filter(
    (field) => !isEmptyAnswer(field.value),
  );
  const completion: FormCompletionRecord = {
    visibleFieldCount: visibleFields.length,
    requiredFieldCount: requiredVisibleFields.length,
    answeredRequiredFieldCount: answeredRequiredFields.length,
    completionPercent:
      requiredVisibleFields.length === 0
        ? 100
        : Math.round(
            (answeredRequiredFields.length / requiredVisibleFields.length) *
              100,
          ),
    complete:
      requiredVisibleFields.length === 0 ||
      answeredRequiredFields.length === requiredVisibleFields.length,
    pendingFieldIds: requiredVisibleFields
      .filter((field) => isEmptyAnswer(field.value))
      .map((field) => field.field.id),
  };

  return {
    version,
    sections,
    visibleFieldIds: visibleFields.map((field) => field.field.id),
    hiddenFieldIds: version.fields
      .filter(
        (field) =>
          !visibleFields.some((visible) => visible.field.id === field.id),
      )
      .map((field) => field.id),
    completion,
  };
}

export function validateFormSubmissionAgainstForm(
  form: FormRecord,
  input: FormSubmissionInput,
) {
  const engineForm = ensureFormEngineState(form);
  const version = engineForm.currentVersion!;
  const answersByFieldId = new Map(
    input.answers.map((answer) => [answer.fieldId, answer.value]),
  );
  const renderable = resolveRenderableForm(
    engineForm,
    input.answers.map((answer) => ({
      fieldId: answer.fieldId,
      fieldKey:
        version.fields.find((field) => field.id === answer.fieldId)?.fieldKey ??
        answer.fieldId,
      value: answer.value,
    })),
  );
  const visibleFieldIds = new Set(renderable.visibleFieldIds);
  const issues: FormEngineIssue[] = [];
  const normalizedAnswers: FormSubmissionInput['answers'] = [];

  for (const answer of input.answers) {
    const field = version.fields.find(
      (candidate) => candidate.id === answer.fieldId,
    );

    if (!field) {
      issues.push({
        code: 'unknown_field',
        message: `Unknown form field "${answer.fieldId}".`,
        fieldId: answer.fieldId,
      });
      continue;
    }

    if (!visibleFieldIds.has(field.id)) {
      issues.push({
        code: 'hidden_field_answer',
        message: `Field "${field.fieldKey}" is hidden in the current answer path and its value was dropped.`,
        fieldId: field.id,
      });
      continue;
    }

    const fieldIssues = validateFieldValue(field, answer.value);

    issues.push(...fieldIssues);
    normalizedAnswers.push(answer);
  }

  for (const section of renderable.sections) {
    for (const field of section.fields) {
      if (!field.visible || !field.required) {
        continue;
      }

      const answerValue = answersByFieldId.get(field.field.id) ?? null;

      if (isEmptyAnswer(answerValue)) {
        issues.push({
          code: 'missing_required_answer',
          message: `Field "${field.field.fieldKey}" is required.`,
          fieldId: field.field.id,
        });
      }
    }
  }

  return {
    valid: issues.every((issue) => issue.code !== 'hidden_field_answer')
      ? issues.length === 0
      : issues.filter((issue) => issue.code !== 'hidden_field_answer')
          .length === 0,
    issues,
    normalizedInput: {
      answers: normalizedAnswers,
    } satisfies FormSubmissionInput,
    renderable,
  };
}
